import { store } from "../core/store";
import { socket } from "../core/socket";
import { GameState } from "../../shared/types";
import type { GameSnapshot, PlayerDTO } from "../../shared/types";
import { openColorPicker } from "./colorPicker";

export function initPlayerList(): void {
  const container = document.getElementById("player-list")!;
  let lastSignature = "";
  render(container, [], null, false, null, false, 0, false);
  store.subscribe((state) => {
    const snapshot = state.snapshot;
    const players = snapshot?.players ?? [];
    const scoresHidden = isFinalStretch(snapshot) || state.config?.scoresVisible === false;
    const drawingArtistId = snapshot?.state === GameState.DRAWING ? snapshot.currentArtistId : null;
    const anonymousSpectatorCount = snapshot?.anonymousSpectatorCount ?? 0;
    // Own-swatch recoloring is only offered in the waiting room - see colorPicker.ts.
    const isWaitingRoom = snapshot?.state === GameState.WAITING_ROOM;
    const signature =
      JSON.stringify(players) +
      state.myPlayerId +
      state.isHost +
      scoresHidden +
      drawingArtistId +
      anonymousSpectatorCount +
      isWaitingRoom;
    if (signature === lastSignature) return;
    lastSignature = signature;
    render(
      container,
      players,
      state.myPlayerId,
      state.isHost,
      drawingArtistId,
      scoresHidden,
      anonymousSpectatorCount,
      isWaitingRoom
    );
  });
}

/** True from the moment the last turn of the last round finishes through the end of
 * the victory ceremony - scores are hidden in the sidebar during this stretch so the
 * ceremony's last-to-first reveal isn't spoiled. */
function isFinalStretch(snapshot: GameSnapshot | null | undefined): boolean {
  if (!snapshot) return false;
  if (snapshot.state === GameState.VICTORY_CEREMONY) return true;
  if (snapshot.state !== GameState.SCORE_CALCULATION && snapshot.state !== GameState.NEXT_ARTIST) return false;
  return (
    snapshot.totalRounds > 0 &&
    snapshot.totalTurnsInRound > 0 &&
    snapshot.round === snapshot.totalRounds &&
    snapshot.turnInRound === snapshot.totalTurnsInRound
  );
}

function render(
  container: HTMLElement,
  players: PlayerDTO[],
  myPlayerId: string | null,
  viewerIsHost: boolean,
  drawingArtistId: string | null,
  scoresHidden: boolean,
  anonymousSpectatorCount: number,
  isWaitingRoom: boolean
): void {
  container.innerHTML = "";
  const list = document.createElement("div");
  list.className = "player-badges";

  for (const player of players) {
    const badge = document.createElement("div");
    badge.className = "player-badge";
    if (!player.connected) badge.classList.add("disconnected");
    if (player.id === myPlayerId) badge.classList.add("is-you");
    if (player.hasGuessedCorrectly) badge.classList.add("guessed-correctly");
    if (player.id === drawingArtistId) badge.classList.add("is-drawing");
    badge.style.setProperty("--player-color", player.color);

    const canRecolor = isWaitingRoom && player.id === myPlayerId;
    const swatch = document.createElement(canRecolor ? "button" : "span");
    swatch.className = "player-color-swatch";
    swatch.style.backgroundColor = player.color;
    if (canRecolor) {
      (swatch as HTMLButtonElement).type = "button";
      swatch.classList.add("player-color-swatch--editable");
      swatch.title = "Change your color";
      swatch.addEventListener("click", () => openColorPicker(player.color));
    }
    badge.appendChild(swatch);

    const nameWrap = document.createElement("span");
    nameWrap.className = "player-name-wrap";
    const name = document.createElement("span");
    name.className = "player-name";
    name.textContent = player.name;
    nameWrap.appendChild(name);
    if (player.isHost) {
      const hostTag = document.createElement("span");
      hostTag.className = "host-tag";
      hostTag.textContent = "★";
      hostTag.title = "Host";
      nameWrap.appendChild(hostTag);
    }
    badge.appendChild(nameWrap);

    if (player.isSpectator) {
      const specTag = document.createElement("span");
      specTag.className = "spectator-tag";
      specTag.textContent = "spectating";
      badge.appendChild(specTag);
    } else {
      const score = document.createElement("span");
      score.className = "player-score";
      if (scoresHidden) {
        score.classList.add("player-score--hidden");
        score.textContent = "?";
        score.title = "Scores are hidden until the victory reveal";
      } else {
        score.textContent = String(player.score);
      }
      badge.appendChild(score);
    }

    if (!player.connected) {
      const status = document.createElement("span");
      status.className = "connection-tag";
      status.textContent = "offline";
      badge.appendChild(status);
    }

    if (viewerIsHost && player.id !== myPlayerId) {
      if (player.connected) {
        const transferBtn = document.createElement("button");
        transferBtn.type = "button";
        transferBtn.className = "transfer-host-button";
        transferBtn.textContent = "⇧";
        transferBtn.title = `Make ${player.name} host`;
        transferBtn.addEventListener("click", () => {
          if (confirm(`Make ${player.name} the host? You'll lose host privileges.`)) {
            socket.emit("host:transfer", player.id);
          }
        });
        badge.appendChild(transferBtn);
      }

      const kickBtn = document.createElement("button");
      kickBtn.type = "button";
      kickBtn.className = "kick-button";
      kickBtn.textContent = "✕";
      kickBtn.title = `Remove ${player.name}`;
      kickBtn.addEventListener("click", () => {
        if (confirm(`Remove ${player.name} from the game?`)) {
          socket.emit("host:kickPlayer", player.id);
        }
      });
      badge.appendChild(kickBtn);
    }

    list.appendChild(badge);
  }

  container.appendChild(list);

  if (anonymousSpectatorCount > 0) {
    const footer = document.createElement("div");
    footer.className = "spectator-count-footer";
    footer.textContent = `Spectators: ${anonymousSpectatorCount}`;
    container.appendChild(footer);
  }
}
